import React, { useState, useRef, useEffect, useCallback, useImperativeHandle } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import htmlDocx from "html-docx-js/dist/html-docx";
import { saveAs } from "file-saver";
import "./ChatInterface.css";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import CampaignIcon from "@mui/icons-material/Campaign";
import DoneIcon from "@mui/icons-material/Done";
import SquareIcon from "@mui/icons-material/Square";
import { ClearIcon } from "@mui/x-date-pickers";
import blackcopychat from "../../../Assets/blackcopychat.png";
import blacktickchat from "../../../Assets/blacktickchat.png";
import whitecopychat from "../../../Assets/whitecopychat.png";
import whitetickchat from "../../../Assets/whitetickchat.png";
import orbitPulsatingIcon from "../../../Assets/orbit-o-logo.png";
import { useConfirmNavigation } from "../useNavigationBlocker";
import useOrbitIntelChatSocket from "../hooks/useOrbitIntelChatSocket";
import useDeepInsights from "../hooks/useDeepInsights";
import OrbitIntelChatFeedbackForm from "../feedback/OrbitIntelChatFeedbackForm";

const ChatInterface = React.forwardRef(({
  userId,
  userProfileData,
  
  
  
  inputDisabled,
  setInputDisabled,
  handleOpenDisclaimer,
  onNewChat,
}, ref) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [openPopover, setOpenPopover] = useState(false);
  const [openLogPopup, setOpenLogPopup] = useState(false);
  const [selectedLog, setSelectedLog] = useState("");

  const inputRef = useRef(null);
  const chatBottomRef = useRef(null);
  const chatScrollRef = useRef(null);
  const popRef = useRef(null);
  const popupRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const { deepInsights, setDeepInsights, logs, handleToggle, handleLogs, clearLogs } = useDeepInsights();
  const deepInsightsRef = useRef(deepInsights);
  useEffect(() => { deepInsightsRef.current = deepInsights; }, [deepInsights]);

  const handleResponse = useCallback((aiResponse) => {
    setMessages((prev) => [
      ...prev,
      {
        content: aiResponse,
        sender: "assistant",
        isMarkdown: true,
      },
    ]);
    clearLogs();
    setIsLoading(false);
    setIsGenerating(false);
    setInputDisabled(false);
    setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  }, [clearLogs, setInputDisabled]);

  const { sendMessage, stop, reconnect, sessionIdRef, isConnected, error } = useOrbitIntelChatSocket({
    userId,
    userProfileData,
    deepInsightsRef,
    onResponse: handleResponse,
    onToggle: handleToggle,
    onLogs: handleLogs,
    setInputDisabled,
  });

  const scrollToBottom = () => {
    chatBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages, isLoading]);

  useEffect(() => {
    const container = chatScrollRef.current;
    if (!container) return;
    const handleScroll = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      setShowScrollButton(distanceFromBottom > 100);
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const closeOnClickOutside = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpenPopover(false);
    };
    document.addEventListener("mousedown", closeOnClickOutside);
    return () => document.removeEventListener("mousedown", closeOnClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) setOpenLogPopup(false);
    };
    if (openLogPopup) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openLogPopup]);

  useConfirmNavigation(
    inputDisabled,
    "Leaving this chat will cancel report generation. Are you sure you want to exit?"
  );

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (inputDisabled) {
        e.preventDefault();
        e.returnValue = "Leaving this chat will cancel report generation. Are you sure you want to exit?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [inputDisabled]);

  const handleSendMessage = () => {
    if (!inputMessage.trim() || inputDisabled) return;
    setMessages((prev) => [
      ...prev,
      {
        content: inputMessage,
        sender: "user",
      },
    ]);
    setIsLoading(true);
    setIsGenerating(true);
    setInputDisabled(true);
    sendMessage(inputMessage);
    setInputMessage("");
    setTimeout(scrollToBottom, 100);
  };

  const handleEnterKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleStopButton = () => {
    stop();
    setIsGenerating(false);
    setIsLoading(false);
    setInputDisabled(false);
    clearLogs();
  };

  const handleNewChatInternal = () => {
    setMessages([]);
    clearLogs();
    setDeepInsights(false);
    setInputMessage("");
    setIsLoading(false);
    setIsGenerating(false);
    reconnect();
    if (onNewChat) onNewChat();
  };

  useImperativeHandle(ref, () => ({ newChat: handleNewChatInternal }));

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1000);
  };

  const handleDownloadDocx = (content) => {
    const html = renderToStaticMarkup(
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    );

    const htmlTemplate = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <title>Orbit Intel Chat Report</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 20px; font-size: 14px; line-height: 1.6; color: #222; }
      h1, h2, h3 { color: #17202e; }
      p { margin: 0 0 8px; }
      ul, ol { padding-left: 20px; margin: 0 0 8px; }
      li { margin-bottom: 4px; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; margin: 8px 0; }
      th { background-color: #f2f2f2; font-weight: bold; }
      th, td { border: 1px solid #999; padding: 6px; font-size: 12px; word-break: break-word; }
      tr { page-break-inside: avoid; }
      a { color: blue; }
      code { background: #f3f4f6; padding: 2px 4px; font-size: 13px; }
      pre { background: #1e2230; color: #e2e8f0; padding: 12px; border-radius: 4px; }
    </style>
  </head>
  <body>${html}</body>
</html>`;

    const blob = htmlDocx.asBlob(htmlTemplate);
    saveAs(blob, "OrbitIntelChat-Report.docx");
  };

  const handleToggleDeepInsights = () => {
    const newValue = !deepInsights;
    setDeepInsights(newValue);
    setOpenPopover(false);
  };

  const isEmpty = !messages.length && logs.length === 0;

  return (
    <div className="orbit-chat-main-start-inner">
      <div className="chat-main-ai-inner">
        {/* Research logs panel */}
        <div className={logs.length === 0 ? "research-main-container-none" : "research-main-container"}>
          <div className="research-tasks-wrapper">
            {logs.length > 0 && (
              <div className="research-tasks-container">
                <h2 className="research-tasks-heading">Research Tasks</h2>
                <div className="research-tasks-list">
                  {logs.map((log, index) => {
                    const words = log.content?.split(" ") || [];
                    const short = words.slice(0, 4).join(" ");
                    const isLong = words.length > 4;
                    return (
                      <div key={index} className="research-task-item">
                        <span className="task-label task-label--completed">
                          • {short}
                          {isLong && (
                            <span
                              className="read-more-link"
                              onClick={() => { setSelectedLog(log.content); setOpenLogPopup(true); }}
                            >
                              Read more
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}

                  {openLogPopup && (
                    <div className="log-popup-overlay">
                      <div className="log-popup-box" ref={popupRef}>
                        <p className="ClearIcon-cross-deep">
                          <ClearIcon onClick={() => setOpenLogPopup(false)} />
                        </p>
                        <h3 className="full-log-details">Full Log Details</h3>
                        <p className="full-det-deep">{selectedLog}</p>
                        <div className="close-research-modal-deep">
                          <button className="close-popup-btn" onClick={() => setOpenLogPopup(false)}>Close</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Chat messages */}
        <div className={logs.length === 0 ? "chat-container-ai-inner-none" : "chat-container-ai-inner"}>
          <div className="chat-messages-ai" ref={chatScrollRef}>
            {messages.map((msg, index) => (
              <React.Fragment key={index}>
                <div className={`message-think ${msg.sender}`}>
                  <div className={msg.sender === "user" ? "message-content-chat" : "message-content-chat-assist-deep"}>
                    <div className="copy-res-deep">
                      {msg.sender === "assistant" ? (
                        <>
                          <p onClick={() => handleCopy(msg.content, index)}>
                            <img src={copiedIndex === index ? blacktickchat : blackcopychat} alt="copy" />
                          </p>
                          <p onClick={() => handleDownloadDocx(msg.content)} className="download-docx-btn">
                            <FileDownloadIcon fontSize="small" />
                          </p>
                        </>
                      ) : (
                        <p onClick={() => handleCopy(msg.content, index)}>
                          <img src={copiedIndex === index ? whitetickchat : whitecopychat} alt="copy" />
                        </p>
                      )}
                    </div>
                    <div className={msg.sender === "user" ? "message-text-chat" : "message-text-chat-assist"}>
                      {msg.isMarkdown ? (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm, remarkBreaks]}
                          components={{
                            a: ({ href, children }) => {
                              const text = typeof children === "string" && children.startsWith("mailto:")
                                ? children.slice(7)
                                : children;
                              return <a href={href} target="_blank" rel="noopener noreferrer">{text}</a>;
                            },
                            table: ({ children }) => (
                              <div className="table-box">
                                <div className="table-box-scroll">
                                  <table>{children}</table>
                                </div>
                              </div>
                            ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      ) : (
                        msg.content
                      )}
                    </div>
                  </div>
                </div>
                {msg.sender === "assistant" && index === messages.length - 1 && (
                  <div className="orbit-icon-static">
                    <img src={orbitPulsatingIcon} alt="Orbit Intel" />
                  </div>
                )}
              </React.Fragment>
            ))}

            {isLoading && (
              <div className="typing-message">
                <div className="message-content">
                  <img src={orbitPulsatingIcon} alt="Orbit Intel thinking" className="orbit-icon-pulsating" />
                  <div className="typing-indicator">
                    Thinking
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}

            {isEmpty && (
              <div className="property-card-main-chat">
                <div className="orbit-chat-logo-row">
                  <img src={orbitPulsatingIcon} alt="Orbit Intel" />
                  <span className="orbit-chat-title">Orbit Intel Chat</span>
                </div>
                <p className="how-can-help-orbit">How can I help you today?</p>
              </div>
            )}

            <div ref={chatBottomRef} />
          </div>
        </div>
      </div>

      {showScrollButton && (
        <button
          className="scroll-to-bottom-btn"
          onClick={() => chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' })}
        >
          ↓
        </button>
      )}

      {/* Input area */}
      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <button className="feedback-fab" onClick={() => setIsFeedbackOpen(true)}>
            <i className="fa-solid fa-circle-exclamation"></i>
            <span>Feedback</span>
          </button>

          <OrbitIntelChatFeedbackForm
            open={isFeedbackOpen}
            onClose={() => setIsFeedbackOpen(false)}
            sessionId={sessionIdRef.current}
          />

          <div className="chat-input-form">
            <div className="text-area-property-match">
              <div className="text-area-inner">
                <div className="chatgpt-plus-wrapper" ref={popRef}>
                  <button
                    disabled={Boolean(inputDisabled)}
                    className="chatgpt-plus-btn"
                    onClick={() => setOpenPopover(!openPopover)}
                  >
                    <span className="deep-icon-activate">
                      <AddIcon fontSize="small" className={deepInsights ? "icon-deep-active" : ""} />
                    </span>
                  </button>

                  {openPopover && (
                    <div className="chatgpt-plus-popover">
                      {!deepInsights ? (
                        <div className="popover-item" onClick={handleToggleDeepInsights}>
                          <CampaignIcon /> Deep Insights Mode
                        </div>
                      ) : (
                        <div className="popover-item-activated" onClick={handleToggleDeepInsights}>
                          <CampaignIcon /> Deep Insights Mode <DoneIcon />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {deepInsights && (
                  <span className="deep-insights-chip">
                    <span>Deep Insights</span>
                    <button className="deep-insights-chip-close" onClick={handleToggleDeepInsights}>×</button>
                  </span>
                )}
                <textarea
                  ref={inputRef}
                  className={`chat-box-property-match${deepInsights ? " chat-box-property-match--deep" : ""}`}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleEnterKey}
                  placeholder="Message Orbit Intel Chat"
                  rows={1}
                  disabled={Boolean(inputDisabled)}
                />
                {inputDisabled ? (
                  <button type="button" className="textarea-upload-button" onClick={handleStopButton}>
                    <SquareIcon className="stop-icon" />
                  </button>
                ) : (
                  <button type="button" className="textarea-upload-button" onClick={handleSendMessage} disabled={Boolean(inputDisabled)}>
                    <ArrowUpwardIcon className={deepInsights ? "icon-deep-active" : ""} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="input-help">
            {error && (
              <p className="disc-orbit-chat" style={{ color: '#e53935' }}>{error}</p>
            )}
            {!isConnected && !error && (
              <p className="disc-orbit-chat" style={{ color: '#f59e0b' }}>Connecting to Orbit Intel Chat...</p>
            )}
            <p className="disc-orbit-chat">
              <span className="disc-orbit-chat-bold"> Disclaimer:</span>{" "}
              Information is for guidance only and should not be relied upon without source and human validation.
            </p>
            <p className="disc-orbit-chat">
              Refer to the{" "}
              <span className="disclamer-footer-orbit" onClick={handleOpenDisclaimer}>disclaimers</span>{" "}
              when using Orbit Intel Chat.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ChatInterface;
